import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function ViewCustomer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const res = await axios.get(`/auth/users/customer/${id}`);
        setCustomer(res.data);
      } catch (err) {
        console.error("Error fetching customer details:", err);
        setError("Unable to fetch customer details.");
      }
    };
    fetchCustomer();
  }, [id]);

  if (error) {
    return (
      <div className="p-6 text-center text-red-600 font-semibold">{error}</div>
    );
  }

  if (!customer) {
    return <div className="p-6 text-gray-500 text-center">Loading customer details...</div>;
  }

  return (
    <section className="flex items-center justify-center min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white w-full max-w-5xl p-6 sm:p-10 rounded-2xl shadow-2xl">
        <h1 className="text-3xl font-extrabold text-center text-green-700 mb-10">
          Customer Profile
        </h1>

        <div className="grid gap-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-6">
            <div className="inline-block bg-green-100 text-green-700 font-semibold px-4 py-1 rounded-full text-sm shadow-sm">
              {customer.accountNumber}
            </div>

            <div>
              <h2 className="text-xl font-semibold text-green-800">Customer Details</h2>
              <p className="text-sm text-gray-500">View detailed customer information</p>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 text-gray-700">
            <Info label="First Name" value={customer.first_name} />
            <Info label="Last Name" value={customer.last_name} />
            <Info label="Email" value={customer.email} />
            <Info label="Phone" value={customer.phone} />
            <Info
              label="Date of Birth"
              value={
                customer.dob
                  ? new Date(customer.dob).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                  : "N/A"
              }
            />
            <Info label="Country" value={customer.country} />
            <Info label="Street Address" value={customer.address} />
            <Info label="City" value={customer.city} />
            <Info label="Borough" value={customer.borough} />
            <Info label="State" value={customer.state} />
            <Info label="Zip Code" value={customer.zip} />
            <Info label="Payment Method" value={customer.payment_method} />
            <Info label="Payment Frequency" value={customer.paymentFrequency} />
            <Info label=" Redemption Center Name" value={customer.center_name} />

            {/* Conditional Payment Info */}
            {customer.payment_method === "PayPal" && (
              <Info label="PayPal Email" value={customer.paymentContact} />
            )}

            {customer.payment_method === "ACH" && (
              <>
                <Info label="ACH Account Name" value={customer.ach_account_name} />
                <Info label="ACH Account Number" value={customer.ach_account_number} />
                <Info label="ACH Routing Number" value={customer.ach_routing_number} />
                <Info label="ACH Account Type" value={customer.ach_account_type} />
              </>
            )}

            {customer.payment_method === "Stripe" && (
              <Info label="Stripe Account ID" value={customer.stripe_account_id} />
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4 mt-10">
          <button
            type="button"
            onClick={() => navigate("/redem-customerlist")}
            className="px-6 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition"
          >
            Back to List
          </button>
        </div>
      </div>
    </section>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <span className="font-semibold">{label}:</span>{" "}
      <span className="text-gray-800">{value || "N/A"}</span>
    </div>
  );
}
